import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePracticesList } from '@/settings/services/use-practices-list';
import { useAuthStore } from '@/auth/auth-store';
import { useNavGuard } from '@/shared/store/nav-guard';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { formatClockText } from '@/shared/lib/time-format';
import type { PracticeList } from '@/types/settings-api';

/**
 * PracticeSwitcher — the active-practice dropdown in the scheduling toolbar.
 *
 * Flutter parity: `calendar_screen.dart` practice dropdown
 * (`practiceDropDownWidget`, lines 2840-2902) + `PracticeChangedState`
 * (lines 2436-2453). On change Flutter sets `userData.practiceId` /
 * `userData.practiceName`, persists, and lets the calendar refetch for the
 * new practice. The React equivalent is `setActivePractice` on the auth
 * store — the api-client reads `user.practiceId` at request time, so the
 * next request carries the new `PracticeId` header and every component
 * selecting `user.practiceId` (scheduling-page) re-keys its queries.
 *
 * Self-contained: fetches its own list and reads/writes the auth store, so
 * the toolbar/frame don't have to prop-drill practice data + an onChange.
 *
 * List source mirrors Flutter `handlePracticeList` (2545-2549): the active
 * (non-archived) list from `GET /api/v1/Practice/List/{orgId}`, deduped by
 * `practiceSetupId` and sorted by `practiceName`.
 */
export function PracticeSwitcher({
  organizationId,
  className,
  onOpenSettings,
  compact = false,
}: {
  organizationId: string;
  className?: string;
  /** The top bar's minified look (Antonin, 13 Oct: "minify it even in decent screens"): the practice's picture and a
   *  chevron; the name is in the tooltip and the open menu. */
  compact?: boolean;
  /** A practice's settings, from its row (Antonin, 13 Oct: the gear moved off the calendar toolbar into this menu;
   *  taller rows, the settings button shows on the row's hover). Omitted → no settings button (confined roles). */
  onOpenSettings?: (practiceSetupId: string) => void;
}) {
  const activePracticeId = useAuthStore((s) => s.user?.practiceId);
  const activePracticeName = useAuthStore((s) => s.user?.practiceName);
  const setActivePractice = useAuthStore((s) => s.setActivePractice);
  const runGuarded = useNavGuard((s) => s.run);

  const { data: rawPractices } = usePracticesList(organizationId, false);

  // Flutter `handlePracticeList`: dedupe by practiceSetupId, sort by name.
  const practices = useMemo(() => {
    const list = (rawPractices ?? []).filter(
      (p): p is PracticeList & { practiceSetupId: string } => !!p.practiceSetupId,
    );
    const seen = new Set<string>();
    const unique = list.filter((p) => {
      if (seen.has(p.practiceSetupId)) return false;
      seen.add(p.practiceSetupId);
      return true;
    });
    return unique.sort((a, b) =>
      (a.practiceName ?? '').localeCompare(b.practiceName ?? ''),
    );
  }, [rawPractices]);

  // Label shown on the trigger: prefer the matched list row, fall back to the
  // store name (covers the window before the list resolves) and finally a
  // placeholder.
  const activePractice = practices.find((p) => p.practiceSetupId === activePracticeId);
  const triggerLabel =
    activePractice?.practiceName ?? activePracticeName ?? 'Select practice';
  const triggerImage = activePractice?.practiceImageURL ?? null;

  const handleChange = (practiceSetupId: string | null) => {
    if (!practiceSetupId) return;
    const next = practices.find((p) => p.practiceSetupId === practiceSetupId);
    if (!next) return;
    // A switch changes every practice-scoped screen, so it passes the unsaved-changes guard like a navigation does
    // (Antonin, 13 Oct: the practice is an app-wide setting, switchable from any page).
    runGuarded(() => setActivePractice({
      practiceSetupId: next.practiceSetupId,
      practiceName: next.practiceName,
    }));
  };

  return (
    <Select value={activePracticeId ?? undefined} onValueChange={handleChange}>
      <SelectTrigger
        aria-label={`Active practice: ${triggerLabel}`}
        title={compact ? triggerLabel : undefined}
        className={cn(compact && 'gap-1 px-1 [&_[data-slot=select-value]]:sr-only',
          'h-10 max-w-[224px] gap-2 border-none bg-transparent px-2 shadow-none hover:bg-grey-100',
          className,
        )}
      >
        <PracticeAvatar name={triggerLabel} imageUrl={triggerImage} />
        <SelectValue>
          <span className="truncate text-sm font-medium text-text-primary">
            {triggerLabel}
          </span>
        </SelectValue>
      </SelectTrigger>
      {/* The menu (Antonin, 13 Oct: "the rounded corners sticking on the edges … terrible", "selected state looks like
          shite"): 6 px inside the panel so every row is an inset 8 px pill, never touching the edge; 12 px panel corners. */}
      <SelectContent className="min-w-[440px] max-w-[480px] rounded-xl p-2 shadow-[0_12px_32px_rgba(21,27,32,0.14),0_2px_6px_rgba(21,27,32,0.06)] ring-[var(--color-grey-300)]">
        {practices.map((p) => (
          <SelectItem key={p.practiceSetupId} value={p.practiceSetupId} className={cn(
              'group/practice my-1 min-h-16 items-start rounded-lg py-2.5 pl-3 pr-40 [&>*:first-child]:min-w-0 [&>*:first-child]:shrink [&>*:first-child]:items-start [&>*:first-child]:gap-3',
              // Highlight: a quiet grey that keeps each line's own ink (the shadcn default turned every line dark).
              'focus:bg-[var(--color-grey-100)]',
              // Selected: the practice you are in. A light brand tint, the name in brand ink, the check in brand blue.
              'data-[selected]:bg-[var(--color-blue-100)] data-[selected]:focus:bg-[color-mix(in_srgb,var(--color-blue-100)_70%,var(--color-blue-200))]',
              '[&>span:last-child]:right-3 [&>span:last-child]:top-4 [&>span:last-child]:text-[var(--color-brand-primary)]',
            )}>
            {/* A richer row (Antonin, 13 Oct: "a bit bigger and more rich in data"): the practice's photo, its name
                (and Primary), the address, today's hours and how many locations it books in. */}
            <PracticeAvatar name={p.practiceName ?? ''} imageUrl={p.practiceImageURL} size="lg" />
            <span className="flex min-w-0 flex-col">
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate text-[length:var(--type-body-strong-size)] font-medium leading-[var(--type-body-strong-line)] text-text-primary! group-data-[selected]/practice:text-[var(--color-blue-700)]!">
                  {p.practiceName ?? '—'}
                </span>
                {p.isPrimary ? (
                  <span className="shrink-0 rounded border border-border-default bg-surface-card px-2 text-[length:var(--type-meta-size)] font-medium leading-4 text-text-secondary!">Primary</span>
                ) : null}
              </span>
              {p.address?.trim() ? (
                <span className="truncate text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] text-text-secondary!">{p.address.trim()}</span>
              ) : null}
              <span className="truncate text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] text-text-muted!">
                {[todayHours(p.workingHours), locationsLine(p.locations)].filter(Boolean).join(' · ') || 'No hours set for today'}
              </span>
            </span>
            {onOpenSettings && (
              // Its own control inside the row: it opens that practice's page and never picks the practice. A secondary
              // button that says where it goes, not a cog (Antonin, 13 Oct).
              <button type="button" aria-label={`Open ${p.practiceName ?? 'the practice'} page`} data-practice-settings
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onPointerUp={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onMouseUp={(e) => e.stopPropagation()}
                // Going to a practice's page makes it the practice you are in (Antonin, 13 Oct): switch, then open,
                // in one step through the unsaved-changes guard (the inner guard then passes straight through).
                onClick={(e) => {
                  e.stopPropagation(); e.preventDefault();
                  runGuarded(() => {
                    setActivePractice({ practiceSetupId: p.practiceSetupId, practiceName: p.practiceName });
                    onOpenSettings(p.practiceSetupId);
                  });
                }}
                className="pointer-events-auto absolute right-10 top-1/2 inline-flex h-8 -translate-y-1/2 items-center gap-1 rounded-md border border-border-default bg-surface-card px-3 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary opacity-0 transition-[opacity,color,border-color] hover:border-border-strong hover:text-text-primary focus-visible:opacity-100 group-hover/practice:opacity-100 group-data-[highlighted]/practice:opacity-100">
                Practice page
                <ArrowRight className="pointer-events-none size-4" aria-hidden />
              </button>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const BRIDGE_WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** "Open today 08:00 – 18:00" from the practice's working hours (single days or Mon–Fri ranges), in the time format. */
function todayHours(hours: PracticeList['workingHours']): string | null {
  const today = new Date().getDay();
  const idx = (d?: string | null) => (d ? BRIDGE_WEEKDAYS.indexOf(d.trim().toLowerCase()) : -1);
  const covers = (h: NonNullable<PracticeList['workingHours']>[number]) => {
    const one = idx(h.weekday);
    if (one >= 0) return one === today;
    const a = idx(h.startWeekDay), b = idx(h.endWeekDay);
    if (a < 0) return false;
    if (b < 0) return a === today;
    return a <= b ? today >= a && today <= b : today >= a || today <= b;
  };
  const slot = (hours ?? []).find((h) => covers(h) && h.startTime && h.endTime);
  if (!slot) return (hours ?? []).length ? 'Closed today' : null;
  return `Open today ${formatClockText(slot.startTime!)} – ${formatClockText(slot.endTime!)}`;
}

function locationsLine(locations: PracticeList['locations']): string | null {
  const n = (locations ?? []).length;
  return n ? `${n} ${n === 1 ? 'location' : 'locations'}` : null;
}

function PracticeAvatar({ name, imageUrl, size = 'sm' }: { name: string; imageUrl: string | null; size?: 'sm' | 'lg' }) {
  const initial = name.trim().charAt(0).toUpperCase() || 'P';
  return (
    <Avatar size={size} className={cn('shrink-0', size === 'lg' && 'rounded-lg after:rounded-lg [&_img]:rounded-lg [&_[data-slot=avatar-fallback]]:rounded-lg')}>
      {imageUrl ? <AvatarImage src={imageUrl} alt={name} /> : null}
      <AvatarFallback className="bg-grey-200 text-size-2xs text-text-muted">
        {initial}
      </AvatarFallback>
    </Avatar>
  );
}
